const getSsoToken = async () => {
    if (!window.riskalive || !window.riskalive.sso) return null;
    for (let i = 0; i < 3; i++) {
        const token = await window.riskalive.sso.getToken();
        if (token) return token;
        await new Promise(r => setTimeout(r, 200));
    }
    return null;
};

// Minimum interval between OnStreamDelta interop calls. Foundry emits deltas at
// token rate; forwarding each one would flood the Blazor render loop, so text is
// accumulated and flushed at most every FLUSH_MS (plus a final forced flush).
const FLUSH_MS = 80;

// The in-flight turn's abort handle. Only one turn streams at a time per surface
// (the send button is disabled while loading), so a single module-level controller
// is enough and avoids handing an opaque id back to Blazor.
let controller = null;

// Aborts the in-flight turn. Cancelling the fetch closes the response body, which
// trips context.RequestAborted on the server and stops the Foundry call — this
// genuinely halts generation rather than just hiding the rest of it. Safe to call
// when nothing is streaming.
const stop = () => {
    if (controller) controller.abort();
};

// Streams a companion chat turn from the SSE endpoint into Blazor. `dotNetRef`
// must expose [JSInvokable] OnStreamDelta(text), OnStreamDone(responseId, model)
// and OnStreamError(status, message). The returned promise resolves only after
// the stream has fully ended and every callback has been delivered, so the
// caller can simply await the interop call.
const stream = async (url, payloadJson, dotNetRef) => {
    controller = new AbortController();
    const signal = controller.signal;
    try {
        const headers = { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' };
        const token = await getSsoToken();
        if (token) headers['Authorization'] = 'Bearer ' + token;

        const resp = await fetch(url, { method: 'POST', headers, body: payloadJson, signal });
        if (!resp.ok || !resp.body) {
            const text = await resp.text().catch(() => '');
            await dotNetRef.invokeMethodAsync('OnStreamError', resp.status, text || resp.statusText);
            return;
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let pending = '';
        let lastFlush = 0;

        const flush = async (force) => {
            if (!pending) return;
            const now = Date.now();
            if (!force && now - lastFlush < FLUSH_MS) return;
            const text = pending;
            pending = '';
            lastFlush = now;
            await dotNetRef.invokeMethodAsync('OnStreamDelta', text);
        };

        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by a blank line; the trailing partial
            // frame stays in the buffer until the next chunk completes it.
            const frames = buffer.split('\n\n');
            buffer = frames.pop();
            for (const frame of frames) {
                for (const line of frame.split('\n')) {
                    if (!line.startsWith('data:')) continue;
                    let evt;
                    try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
                    if (evt.type === 'delta' && evt.text) {
                        pending += evt.text;
                        await flush(false);
                    } else if (evt.type === 'sources' && evt.output) {
                        // An MCP tool result. Flushed ahead of it so the sources never render
                        // before the text that preceded them. OnStreamSources is optional —
                        // surfaces that don't implement it simply ignore the frame.
                        await flush(true);
                        try {
                            await dotNetRef.invokeMethodAsync('OnStreamSources', evt.tool || null, evt.output);
                        } catch { /* surface has no sources handler */ }
                    } else if (evt.type === 'meta') {
                        // Out-of-band facts about the turn (currently the server-side row count
                        // behind an industry AI Review). Optional, like OnStreamSources — a
                        // surface without a meta handler simply ignores the frame.
                        try {
                            await dotNetRef.invokeMethodAsync('OnStreamMeta', evt.matchedRows ?? 0);
                        } catch { /* surface has no meta handler */ }
                    } else if (evt.type === 'done') {
                        await flush(true);
                        await dotNetRef.invokeMethodAsync('OnStreamDone', evt.responseId || null, evt.model || null);
                    } else if (evt.type === 'error') {
                        await flush(true);
                        await dotNetRef.invokeMethodAsync('OnStreamError', 200, evt.message || 'stream error');
                    }
                }
            }
        }
        await flush(true);
    } catch (e) {
        // A user-initiated stop is not a failure. Reporting it through OnStreamError would append
        // "(response interrupted: …)" to an answer the user deliberately cut short. OnStreamStopped
        // is optional in the same way as OnStreamSources/OnStreamMeta — a surface that doesn't
        // implement it simply keeps whatever text arrived.
        if (e && e.name === 'AbortError') {
            try { await dotNetRef.invokeMethodAsync('OnStreamStopped'); } catch { /* surface has no stop handler */ }
        } else {
            try { await dotNetRef.invokeMethodAsync('OnStreamError', 0, (e && e.message) || String(e)); } catch { /* circuit gone */ }
        }
    } finally {
        controller = null;
    }
};

export default { stream, stop };
